/**
 * MP-6 (F324, Paket P3 „Frontend-Projektwahl“): welches Projekt die Oberfläche gerade zeigt.
 *
 * Bis v0.53.5 nahm `App.tsx` das erste Projekt der Liste als einziges (Festlegung 20.08.2026: ein Container, ein
 * Projekt). Mit der Mehrprojektplattform führt ein Container mehrere Projekte, jedes in seiner eigenen Datenbank. Wer
 * per Lesezeichen auf `/projekte/<id>/…` eines anderen Projekts kam, sah im Kopf und in der Seitenleiste weiter das
 * erste - ein Kontextbruch.
 *
 * Die Regeln, in dieser Reihenfolge:
 *   1. Das `:id` der Route, wenn es ein Projekt der Liste ist. Lesezeichen und alte Links bestimmen den Kontext.
 *   2. Sonst das zuletzt gewählte Projekt (Browser-Speicher), wenn es noch in der Liste steht.
 *   3. Sonst das erste der Liste (das bisherige Verhalten).
 * Die Liste ist, was der Server dem Angemeldeten zeigt (PT-0a); ein gemerktes Projekt, das er nicht mehr sieht, zählt
 * nicht.
 */
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ApiClient, Projekt, ProjektDatenbank } from '../api';
import type { ProjectSwitcherEintrag } from './ProjectSwitcher';

/** Ladezustand der Projektliste (PT-0a: eine LEERE Liste ist eine Antwort, kein Ladezustand). */
export type Projektstand = 'laedt' | 'fertig' | 'fehler';

/** Schlüssel im Browser-Speicher. Nur die Projektkennung, nichts weiter. */
export const PROJEKTWAHL_SCHLUESSEL = 'meplora.projektwahl';

/** Das `:id` aus `/projekte/<id>` bzw. `/projekte/<id>/…`; sonst null (auch für `/projekte` selbst). */
export function projektIdAusPfad(pfad: string): string | null {
  const treffer = /^\/projekte\/([^/?#]+)/.exec(pfad);
  if (!treffer) return null;
  try {
    return decodeURIComponent(treffer[1]);
  } catch {
    return treffer[1];
  }
}

/** Regeln 1-3 (siehe oben). */
export function aktivesProjektWaehlen(
  liste: readonly Projekt[],
  routenId: string | null,
  gemerkteId: string | null,
): Projekt | null {
  const aus = (id: string | null) => (id ? liste.find((p) => p.id === id) ?? null : null);
  return aus(routenId) ?? aus(gemerkteId) ?? liste[0] ?? null;
}

/** Das zuletzt gewählte Projekt; ohne Speicher (privates Fenster, gesperrt) null. */
export function gemerktesProjekt(): string | null {
  try {
    return window.localStorage.getItem(PROJEKTWAHL_SCHLUESSEL);
  } catch {
    return null;
  }
}

/** Merkt die Wahl. Scheitert der Speicher, bleibt es bei der Wahl dieser Sitzung. */
export function projektMerken(id: string): void {
  try {
    window.localStorage.setItem(PROJEKTWAHL_SCHLUESSEL, id);
  } catch {
    // ohne Speicher: kein Fehler, nur kein Merken über das Neuladen hinaus
  }
}

/**
 * Wohin der Umschalter führt. Innerhalb eines Projekts auf die Übersicht des neuen - die Unterseite des alten
 * (ein Fachbereich, ein Modell) gibt es im neuen nicht unbedingt. Außerhalb (`/auftraege`, `/maschinen`, `/`) bleibt
 * die Seite, sie folgt dem Kontext; null heißt: nicht navigieren.
 */
export function wechselZiel(pfad: string, neueId: string): string | null {
  if (pfad === '/projekte' || projektIdAusPfad(pfad) !== null) {
    return `/projekte/${encodeURIComponent(neueId)}/uebersicht`;
  }
  return null;
}

/** Die Anzeige der Datenbank eines Projekts (Feld `datenbank` aus GET /api/projects); ohne Feld nichts. */
export function datenbankText(datenbank: ProjektDatenbank | null | undefined): string | null {
  if (datenbank === 'projekt') return 'eigene Projekt-DB';
  if (datenbank === 'dienst') return 'Dienst-DB';
  return null;
}

/** Ein Projekt als Eintrag des Umschalters: Nummer – Name, ACC-Region, Datenbank. */
export function umschalterEintrag(p: Projekt): ProjectSwitcherEintrag {
  return {
    id: p.id,
    bezeichnung: [p.number, p.name].filter(Boolean).join(' – ') || p.id,
    region: p.accRegion,
    datenbank: datenbankText(p.datenbank),
  };
}

export interface Projektwahl {
  /** Alle Projekte, die der Angemeldete sieht. */
  projekte: Projekt[];
  /** Das aktive Projekt (Regeln 1-3) oder null. */
  projekt: Projekt | null;
  stand: Projektstand;
  /** Die volle Liste für den Umschalter. */
  eintraege: ProjectSwitcherEintrag[];
  /** Das aktive Projekt als Eintrag (Kopfzeile, Brotkrumen). */
  eintrag: ProjectSwitcherEintrag | null;
  /** Wechsel aus dem Umschalter: merken und, innerhalb eines Projekts, auf die Übersicht des neuen. */
  wechseln: (id: string) => void;
}

/**
 * Der Projektkontext der ganzen Oberfläche: lädt die Liste einmal nach der Anmeldung und leitet das aktive Projekt aus
 * Route und Merkwert ab. Kopfzeile, Seitenleiste und Auftragsliste lesen alle dieses eine Ergebnis - so kann der Kopf
 * nicht ein anderes Projekt zeigen als die Seite.
 */
export function useProjektwahl(api: Pick<ApiClient, 'projekte'>, angemeldet: boolean): Projektwahl {
  const pfad = useLocation().pathname;
  const navigiere = useNavigate();
  const [projekte, setProjekte] = useState<Projekt[]>([]);
  const [stand, setStand] = useState<Projektstand>('laedt');
  const [gemerkt, setGemerkt] = useState<string | null>(() => gemerktesProjekt());

  useEffect(() => {
    if (!angemeldet) return;
    api.projekte()
      .then((liste) => { setProjekte(liste); setStand('fertig'); })
      .catch(() => { setProjekte([]); setStand('fehler'); });
  }, [api, angemeldet]);

  const routenId = projektIdAusPfad(pfad);
  const projekt = useMemo(
    () => aktivesProjektWaehlen(projekte, routenId, gemerkt),
    [projekte, routenId, gemerkt],
  );

  // Kam das Projekt aus der Route (Lesezeichen, Link), gilt es ab jetzt auch als zuletzt gewählt: der Sprung auf
  // /auftraege bleibt dann in diesem Projekt.
  useEffect(() => {
    if (projekt && projekt.id === routenId && projekt.id !== gemerkt) {
      projektMerken(projekt.id);
      setGemerkt(projekt.id);
    }
  }, [projekt, routenId, gemerkt]);

  const wechseln = useCallback((id: string) => {
    projektMerken(id);
    setGemerkt(id);
    const ziel = wechselZiel(pfad, id);
    if (ziel) navigiere(ziel);
  }, [pfad, navigiere]);

  const eintraege = useMemo(() => projekte.map(umschalterEintrag), [projekte]);
  const eintrag = useMemo(() => (projekt ? umschalterEintrag(projekt) : null), [projekt]);

  return {
    projekte, projekt, stand, eintraege, eintrag, wechseln,
  };
}
